import type { EntityAdminRecord } from '@src/components/EntityAdmin/types';
import { isEqual } from 'lodash-es';
import type { NonDividerDrawerOption } from './dividerUtils';

export interface DrawerContentProps<
  TDrawerOption extends NonDividerDrawerOption<TDrawerRecord>,
  TDrawerRecord extends EntityAdminRecord,
> {
  drawerOption: TDrawerOption;
  form: TDrawerRecord;
  originalEntity: TDrawerRecord | undefined;
  isEditing: boolean;
  inheritedValue: string | undefined;
  onFormUpdate: (field: keyof TDrawerRecord, value: string | boolean, suppressTouch?: boolean) => void;
}

/**
 * Shared memo comparison function for drawer content components.
 * Compares all props that affect rendering: drawerOption, form, originalEntity, isEditing, and inheritedValue.
 *
 * This is necessary because getIsDisabled, getInputPrefix, getInputSuffix, and getDropdownOptions
 * can depend on any field in the form, not just the specific field this component handles.
 */
export const drawerContentMemoComparison = <
  TDrawerOption extends NonDividerDrawerOption<TDrawerRecord>,
  TDrawerRecord extends EntityAdminRecord,
>(
  prevProps: DrawerContentProps<TDrawerOption, TDrawerRecord>,
  nextProps: DrawerContentProps<TDrawerOption, TDrawerRecord>
) => {
  return (
    prevProps.drawerOption === nextProps.drawerOption &&
    prevProps.isEditing === nextProps.isEditing &&
    prevProps.inheritedValue === nextProps.inheritedValue &&
    isEqual(prevProps.originalEntity, nextProps.originalEntity) &&
    isEqual(prevProps.form, nextProps.form)
  );
};
